import { useState } from 'react';

import { Accordion, ActionIcon, Group, Text } from '@mantine/core';

import { IconChevronRight } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';

import type { DocumentChunk } from '../../../../../shared/entities/DocumentChunk';
import type {
  BidResponseDocumentSearchResult,
  WorkspaceDocumentSearchResult,
} from '../../../../../shared/entities/Search';
import { useSourceSidePanel } from '../../../../documents/hooks/useSourceSidePanel';
import { getUniqueDocumentIdString } from '../utils/vaultSearchUtils';
import { VaultSearchResultItemLabel } from './VaultSearchResultItemLabel';
import { useGetVaultSearchByIdQuery } from '../hooks/vaultSearch.hook';

export function VaultSearchResultItem({
  result,
}: {
  result: BidResponseDocumentSearchResult | WorkspaceDocumentSearchResult;
}) {
  const uniqueDocumentIdString = getUniqueDocumentIdString(result);

  return (
    <Accordion.Item value={uniqueDocumentIdString} key={uniqueDocumentIdString}>
      <Accordion.Control>
        <VaultSearchResultItemLabel result={result} />
      </Accordion.Control>
      <Accordion.Panel>
        <ResultChunkList chunks={result.chunks} />
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function ResultChunkList({
  chunks,
}: {
  chunks: BidResponseDocumentSearchResult['chunks'];
}) {
  const { id } = useParams();
  const searchId = Number(id);
  const {
    data: { results },
  } = useGetVaultSearchByIdQuery(searchId);

  const { openPanel } = useSourceSidePanel({ documents: results });
  const [selectedChunkExtract, setSelectedChunkExtract] = useState<string>('');

  const handleOpenPanel = (source: DocumentChunk) => {
    openPanel(source);
    setSelectedChunkExtract(source.pageContent);
  };

  return chunks.map((chunk, index) => (
    <ChunkItem
      key={chunk.pageContent}
      index={index}
      chunk={chunk}
      onOpen={handleOpenPanel}
      isActive={selectedChunkExtract === chunk.pageContent}
    />
  ));
}

type ChunkItemProps = {
  index: number;
  chunk: DocumentChunk;
  onOpen: (source: DocumentChunk) => void;
  isActive: boolean;
};

function ChunkItem({ index, chunk, onOpen, isActive }: ChunkItemProps) {
  return (
    <Group
      noWrap
      p="01"
      pl="02"
      spacing="01"
      onClick={() => onOpen(chunk)}
      sx={theme => ({
        backgroundColor: isActive ? theme.colors.primary[1] : 'transparent',
        borderRadius: theme.radius.md,
        cursor: 'pointer',
        ':hover': {
          backgroundColor: theme.colors.primary[1],
        },
      })}
    >
      <Text variant="sm" fw={400} c="gray.6" w="98%" truncate>
        {`Extrait ${index + 1} : ${chunk.pageContent}`}
      </Text>

      <ActionIcon variant="transparent" size="sm">
        <IconChevronRight size={16} />
      </ActionIcon>
    </Group>
  );
}
